import React, { useEffect, useRef } from 'react';
import JsVectorMap from 'jsvectormap/dist/jsvectormap.js';
import 'jsvectormap/dist/maps/world.js';
import 'jsvectormap/dist/jsvectormap.css';

// Thin React wrapper around jsvectormap's world map, in three modes:
//   markers        — array of { name, coords: [lat, lng] } dots (dashboard "Users Geolocation")
//   highlightCodes — array of ISO-3166-1 alpha-2 codes to fill in (storefront "World Users")
//   series         — { values: { US: 120, IN: 4300, ... }, legendTitle } choropleth,
//                     shaded by value (Reports > Area Sales)
// jsvectormap is a vanilla-JS/DOM library, not a React component, so it's
// instantiated imperatively on mount and torn down on unmount — same pattern
// as any non-React widget wrapped for React (a chart lib, a rich text editor).
export default function WorldMap({ markers, highlightCodes, series, height = 320, color = '#1a3c5e', onRegionClick }) {
  const elRef = useRef();
  const mapRef = useRef();

  useEffect(() => {
    if (!elRef.current) return undefined;

    // jsvectormap is a vanilla-JS library that injects its own SVG into this
    // div; its destroy() doesn't reliably strip that SVG back out. Under
    // React 18 StrictMode, React deliberately mounts -> cleans up -> mounts
    // this effect once in dev to catch exactly this kind of bug — the stale
    // SVG from the first mount was being left behind, so the second mount's
    // map got appended right next to it, rendering as two stacked maps (the
    // "Users Geolocation" admin map and the storefront "World Users" map,
    // since both go through this shared component). Force the container
    // empty right before creating a new instance so this can't happen
    // regardless of what destroy() actually cleans up.
    elRef.current.innerHTML = '';

    mapRef.current = new JsVectorMap({
      selector: elRef.current,
      map: 'world',
      zoomButtons: true,
      zoomOnScroll: false,
      showTooltip: true,
      backgroundColor: 'transparent',
      regionStyle: {
        initial: { fill: '#e2e8f0', stroke: '#fff', strokeWidth: 0.5 },
        hover: { fillOpacity: 0.8, cursor: 'pointer' },
        selected: { fill: color },
      },
      selectedRegions: highlightCodes || [],
      markers: markers || [],
      markerStyle: {
        initial: { r: 6, fill: color, fillOpacity: 1, stroke: '#fff', strokeWidth: 2 },
        hover: { fill: color, cursor: 'pointer' },
      },
      markerLabelStyle: { initial: { fontFamily: 'inherit', fontSize: 11, fill: '#374151' } },
      ...(series?.values && Object.keys(series.values).length ? {
        series: {
          regions: [{
            values: series.values,
            scale: series.scale || ['#dbe9f5', color],
            normalizeFunction: 'polynomial',
            legend: { title: series.legendTitle || '', vertical: true },
          }],
        },
      } : {}),
      onRegionTooltipShow: series?.tooltipText ? (event, tooltip, code) => {
        const text = series.tooltipText(code);
        if (text) tooltip.text(text);
      } : undefined,
      onRegionClick: onRegionClick ? (event, code) => onRegionClick(code) : undefined,
    });

    return () => {
      try { mapRef.current?.destroy(); } catch (_) {}
      if (elRef.current) elRef.current.innerHTML = '';
    };
  }, [color, JSON.stringify(markers), JSON.stringify(highlightCodes), JSON.stringify(series?.values), series?.legendTitle]);

  return <div ref={elRef} style={{ width: '100%', height }} />;
}
